/**
 * What an open place reads and does: its data (read again whenever its wire says so, an older read never
 * replacing a newer one), who is typing, and a room's lines with the sends still on their way, a heart
 * the person just pressed and a vote they just made drawn at once.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Attachment } from "../../../shared/types";
import type { TalkMessage, TalkSendRequest } from "../../../shared/talk";
import { usePage } from "./context";
import { newClientId } from "./thread";
import { type Typist, usePlaceWire } from "./wires";

const TYPING_MS = 6_000;

export interface Feed<T> {
  data: T | null;
  failed: boolean;
  refresh: () => void;
  /** Who is typing now, or null. An empty name is someone the page cannot name. */
  typing: string | null;
  noteTyping: () => void;
}

const isTypist = (m: TalkMessage, { from, rid }: Typist): boolean => (from === "host" ? m.who.kind === "host" : m.who.kind === "member" && m.who.registrantId === rid);

/** What a place's lines say about a typist: the first name (the host by name, a guest by registration id; empty when unknown) and the lines they have sent. */
export interface Typists {
  nameOf: (who: Typist) => string;
  sentBy: (who: Typist) => string[];
}

export function typistsOf(messages: readonly TalkMessage[] | undefined, hostFirst: string): Typists {
  return {
    nameOf: (who) => {
      if (who.from === "host") return hostFirst;
      const line = who.rid ? messages?.find((m) => isTypist(m, who)) : undefined;
      return line?.who.name.split(/\s+/)[0] ?? "";
    },
    sentBy: (who) => (messages ?? []).filter((m) => isTypist(m, who)).map((m) => m.id),
  };
}

export function useFeed<T>(key: string, load: () => Promise<T>, wirePath: string | null, typists?: (data: T | null) => Typists): Feed<T> {
  const { refreshGuest } = usePage();
  const [data, setData] = useState<T | null>(null);
  const [failed, setFailed] = useState(false);
  const [typing, setTyping] = useState<string | null>(null);
  const reads = useRef(0);
  const applied = useRef(0);
  const loadRef = useRef(load);
  loadRef.current = load;
  const typingOff = useRef<number | undefined>(undefined);
  const typistsRef = useRef(typists);
  typistsRef.current = typists;
  // Why the lines held when the dots came up: a new line from the same person ends them.
  const typist = useRef<{ who: Typist; had: Set<string> } | null>(null);

  const stopTyping = useCallback(() => {
    window.clearTimeout(typingOff.current);
    typist.current = null;
    setTyping(null);
  }, []);

  const refresh = useCallback(() => {
    const n = ++reads.current;
    loadRef
      .current()
      .then((res) => {
        if (n < applied.current) return;
        applied.current = n;
        setData(res);
        setFailed(false);
        const t = typist.current;
        if (t && typistsRef.current?.(res).sentBy(t.who).some((id) => !t.had.has(id))) stopTyping();
      })
      .catch(() => setFailed(true));
  }, [stopTyping]);

  useEffect(() => {
    setData(null);
    setFailed(false);
    stopTyping();
    applied.current = reads.current;
    refresh();
  }, [key, refresh, stopTyping]);

  const dataRef = useRef(data);
  dataRef.current = data;
  const onTyping = useCallback(
    (who: Typist) => {
      const lines = typistsRef.current?.(dataRef.current);
      setTyping(lines?.nameOf(who) ?? "");
      typist.current = { who, had: new Set(lines?.sentBy(who)) };
      window.clearTimeout(typingOff.current);
      typingOff.current = window.setTimeout(stopTyping, TYPING_MS);
    },
    [stopTyping],
  );
  useEffect(() => () => window.clearTimeout(typingOff.current), []);
  // Why the page's counts too: a line here is news to What's new and the badges, which read it again.
  const onWire = useCallback(() => {
    refresh();
    refreshGuest();
  }, [refresh, refreshGuest]);
  const noteTyping = usePlaceWire(wirePath, onWire, onTyping);
  return { data, failed, refresh, typing, noteTyping };
}

interface Pending {
  req: TalkSendRequest;
  message: TalkMessage;
  failed: boolean;
}

export interface Chat {
  messages: TalkMessage[];
  /** Press Send: draws the line at once and sends it once, however often it has to go again. */
  send: (body: string, attachments: Attachment[], replyTo?: string) => void;
  retry: (clientId: string) => void;
  failed: ReadonlySet<string>;
  heart: (m: TalkMessage) => void;
  vote: (m: TalkMessage, choice: number) => void;
}

export function useChat(
  messages: readonly TalkMessage[] | undefined,
  me: { name: string; avatar: string | null },
  api: {
    send: (req: TalkSendRequest) => Promise<unknown>;
    heart: (id: string, on: boolean) => Promise<unknown>;
    vote: (id: string, choice: number) => Promise<unknown>;
  },
  refresh: () => void,
  key: string,
): Chat {
  const [pending, setPending] = useState<Pending[]>([]);
  const [hearts, setHearts] = useState<ReadonlyMap<string, boolean>>(new Map());
  const [votes, setVotes] = useState<ReadonlyMap<string, number>>(new Map());
  useEffect(() => {
    setPending([]);
    setHearts(new Map());
    setVotes(new Map());
  }, [key]);

  const go = useCallback(
    (req: TalkSendRequest) => {
      setPending((p) => p.map((x) => (x.req.clientId === req.clientId ? { ...x, failed: false } : x)));
      api
        .send(req)
        .then(refresh)
        .catch(() => setPending((p) => p.map((x) => (x.req.clientId === req.clientId ? { ...x, failed: true } : x))));
    },
    [api, refresh],
  );
  const send = useCallback(
    (body: string, attachments: Attachment[], replyTo?: string) => {
      const clientId = newClientId();
      const req: TalkSendRequest = { body, clientId, ...(attachments.length ? { attachments } : {}), ...(replyTo ? { replyTo } : {}) };
      const message: TalkMessage = {
        id: clientId,
        who: { kind: "member", registrantId: "", name: me.name, photoUrl: me.avatar, me: true },
        body,
        attachments,
        createdAt: Math.floor(Date.now() / 1000),
        hearts: 0,
        hearted: false,
        replies: 0,
        replyTo: replyTo ?? null,
        pinned: false,
        poll: null,
        call: null,
        clientId,
      };
      setPending((p) => [...p, { req, message, failed: false }]);
      go(req);
    },
    [me.name, me.avatar, go],
  );
  const retry = useCallback(
    (clientId: string) => {
      const p = pending.find((x) => x.req.clientId === clientId);
      if (p) go(p.req);
    },
    [pending, go],
  );

  const heart = useCallback(
    (m: TalkMessage) => {
      const was = hearts.get(m.id) ?? m.hearted;
      setHearts((h) => new Map(h).set(m.id, !was));
      api.heart(m.id, !was).catch(() => setHearts((h) => new Map(h).set(m.id, was)));
    },
    [api, hearts],
  );
  const vote = useCallback(
    (m: TalkMessage, choice: number) => {
      setVotes((v) => new Map(v).set(m.id, choice));
      api
        .vote(m.id, choice)
        .then(refresh)
        .catch(() => setVotes((v) => new Map([...v].filter(([id]) => id !== m.id))));
    },
    [api, refresh],
  );

  const shown = useMemo(() => {
    const server = messages ?? [];
    const have = new Set(server.map((m) => m.clientId).filter(Boolean));
    const base = server.map((m) => {
      const on = hearts.get(m.id);
      const mine = votes.get(m.id);
      let next = m;
      if (on !== undefined && on !== m.hearted) next = { ...next, hearted: on, hearts: Math.max(0, next.hearts + (on ? 1 : -1)) };
      if (mine !== undefined && m.poll && m.poll.mine !== mine) {
        const was = m.poll.mine;
        next = {
          ...next,
          poll: {
            ...m.poll,
            mine,
            voted: was === null ? m.poll.voted + 1 : m.poll.voted,
            options: m.poll.options.map((o, i) => ({ ...o, votes: o.votes + (i === mine ? 1 : 0) - (i === was ? 1 : 0) })),
          },
        };
      }
      return next;
    });
    return [...base, ...pending.filter((p) => !have.has(p.req.clientId)).map((p) => p.message)];
  }, [messages, pending, hearts, votes]);
  const failed = useMemo(() => new Set(pending.filter((p) => p.failed).map((p) => p.req.clientId)), [pending]);
  return { messages: shown, send, retry, failed, heart, vote };
}
